import {useEffect, useRef, useState} from 'react'
import * as Sentry from '@sentry/react'
import {detectBrowser} from 'notability-services-common-frontend'

function generatePersistedAnonymousId() {
  const STORAGE_KEY = 'notability_anonymous_id'
  const TIMESTAMP_KEY = 'notability_anonymous_id_timestamp'
  const EXPIRY_DAYS = 90 // Window of time to track someone anonymously. I.e. we can tell if it's the same user who visited the login page, logged in, signed up, and maybe subscribed if it's within 30 days. Longer than that and the id is going to reset.
  const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000

  try {
    // Try to get existing ID and timestamp from localStorage
    const existingId = localStorage.getItem(STORAGE_KEY)
    const timestampStr = localStorage.getItem(TIMESTAMP_KEY)

    if (existingId && timestampStr) {
      const timestamp = parseInt(timestampStr, 10)
      const now = Date.now()

      if (now - timestamp < EXPIRY_MS) {
        return existingId
      } else {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(TIMESTAMP_KEY)
      }
    }
  } catch (error) {}

  // Generate new UUID-like ID
  const newId = 'anonymous_id_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
  const now = Date.now()

  try {
    // Try to persist both ID and timestamp
    localStorage.setItem(STORAGE_KEY, newId)
    localStorage.setItem(TIMESTAMP_KEY, now.toString())
  } catch (error) {
    // If we can't persist, we can't track the user at all. This will short-circuit singular SDK initialization deliberately
    return undefined
  }

  return newId
}

// these are safe to hardcode because they do not change depending on prod/staging/berry environment and are verified with Singular to be safe to be exposed to the client, despite calling one of the values a secret
const SINGULAR_SDK_KEY = 'ginger_labs_0dda4736'
const SINGULAR_SDK_SECRET = 'd3f7a7007fcd36fc6875664cfb69140b'
const SINGULAR_PRODUCT_ID = 'com.notability.web'

function reportSingularError(
  error: any,
  operation: string,
  additionalContext: {eventName?: SingularNotabilityWebEventName} = {}
) {
  Sentry.captureException(error, {
    tags: {
      component: 'useSingular',
      operation,
      browser: detectBrowser()
    },
    extra: {
      message: `Singular ${operation} error`,
      ...additionalContext
    }
  })
}

interface UseSingularOptions {
  anonymousId?: string
}

type SingularNotabilityWebEventName =
  | 'sng_web_landing'
  | 'sng_web_login'
  | 'sng_web_signup'
  | 'sng_web_subscribe_plus'
  | 'sng_web_subscribe_pro'

/**
 * Custom hook to initialize and manage Singular SDK
 * Should be called once at the app level (e.g. in LoginContext)
 */
export function useSingular({anonymousId}: UseSingularOptions = {}) {
  const [isInitialized, setIsInitialized] = useState(false)
  const currentAnonymousId = useRef<string | undefined>()
  const singularSdkRef = useRef<any>(null)
  const [clientSideFallbackAnonymousId, setClientSideFallbackAnonymousId] = useState<string | undefined>()

  useEffect(() => {
    if (!anonymousId && !clientSideFallbackAnonymousId) {
      setClientSideFallbackAnonymousId(generatePersistedAnonymousId())
    }
  }, [anonymousId, clientSideFallbackAnonymousId])

  const effectiveAnonymousId = anonymousId || clientSideFallbackAnonymousId

  useEffect(() => {
    if (isInitialized || !effectiveAnonymousId) {
      return
    }

    // Dynamically import singular-sdk only in browser environment
    import('singular-sdk')
      .then(({SingularConfig, singularSdk}) => {
        try {
          singularSdkRef.current = singularSdk

          const singularConfig = new SingularConfig(SINGULAR_SDK_KEY, SINGULAR_SDK_SECRET, SINGULAR_PRODUCT_ID)

          singularConfig.withCustomUserId(effectiveAnonymousId)

          singularSdk.init(singularConfig)
          setIsInitialized(true)
        } catch (error) {
          reportSingularError(error, 'init')
        }
      })
      .catch((error) => {
        reportSingularError(error, 'load')
      })
  }, [effectiveAnonymousId, isInitialized])

  useEffect(() => {
    if (!isInitialized || !singularSdkRef.current) return

    if (effectiveAnonymousId && effectiveAnonymousId !== currentAnonymousId.current) {
      try {
        singularSdkRef.current.login(effectiveAnonymousId)
        currentAnonymousId.current = effectiveAnonymousId
      } catch (error) {
        reportSingularError(error, 'anonymous id set')
      }
    } else if (!effectiveAnonymousId && currentAnonymousId.current) {
      try {
        singularSdkRef.current.logout()
        currentAnonymousId.current = undefined
      } catch (error) {
        reportSingularError(error, 'anonymous id cleared')
      }
    }
  }, [effectiveAnonymousId, isInitialized])

  const trackEvent = (eventName: SingularNotabilityWebEventName, args?: Record<string, string | number | boolean>) => {
    if (isInitialized && singularSdkRef.current) {
      try {
        singularSdkRef.current.event(eventName, args)
      } catch (error) {
        reportSingularError(error, 'trackEvent', {
          eventName
        })
      }
    }
  }

  return isInitialized
    ? {
        trackEvent
      }
    : undefined
}
